import { create } from 'zustand';
import type { AuthUserPublic, MeResponse } from '@dsr/shared-types';

// `MeResponse.user` is nullable on the wire (the bootstrap-friendly
// /api/me returns `user: null` when there's no session). Inside the
// store we only hold a Session — the invariant "we have a logged-in
// user" is encoded in the type, so consumers can read `me.user.foo`
// without a per-call null check after gating on `me`.
export type Session = Omit<MeResponse, 'user'> & { user: AuthUserPublic };

interface AuthState {
  me: Session | null;
  loading: boolean;
  setMe: (me: Session | null) => void;
  setLoading: (loading: boolean) => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  me: null,
  loading: true, // bootstrap call decides
  setMe: (me) => set({ me, loading: false }),
  setLoading: (loading) => set({ loading }),
}));

// Mirrors the API-side hasPermission semantics: org-level denial >
// org-level grant > global grant. Pass an `orgId` to consult the
// per-organization override map (MeResponse.org_overrides) for that org;
// omit it for a plain global-permission check.
export function can(perm: string, orgId?: number): boolean {
  const me = useAuthStore.getState().me;
  if (!me) return false;
  if (orgId !== undefined && me.org_overrides) {
    const override = me.org_overrides[String(orgId)];
    if (override?.denied.includes(perm)) return false;
    if (override?.granted.includes(perm)) return true;
  }
  return me.global_permissions.includes(perm);
}

// The server-provided Geoapify key (config.geoapify_api_key on /me), or null
// when no session is loaded yet or the server has no key configured. UI gates
// address verification on a non-null value.
export function geoapifyKey(): string | null {
  const me = useAuthStore.getState().me;
  return me?.config?.geoapify_api_key ?? null;
}
